import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { recouvrementService } from '../../api/services';
import { Tresorerie as TresorerieData } from '../../types';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

/**
 * Prévision de trésorerie (TF-013) : encaissements/décaissements attendus et solde
 * projeté sur l'horizon, à partir d'un solde d'ouverture saisi.
 */
export function Tresorerie() {
  const [solde, setSolde] = useState(0);
  const [mois, setMois] = useState(6);
  const [data, setData] = useState<TresorerieData | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    recouvrementService.getTresorerie(mois, solde).then(setData).finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [mois]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Prévision de trésorerie</h1>
        <p className="page-subtitle">Cash projeté à partir des factures clients et fournisseurs en attente</p>
      </div>

      <div className="card flex flex-col sm:flex-row sm:items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Solde d'ouverture</label>
          <input type="number" className="input-field" value={solde}
                 onChange={(e) => setSolde(Number(e.target.value))} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Horizon (mois)</label>
          <select className="input-field" value={mois} onChange={(e) => setMois(Number(e.target.value))}>
            {[3, 6, 12].map((m) => <option key={m} value={m}>{m} mois</option>)}
          </select>
        </div>
        <button onClick={load} className="btn-primary">Actualiser</button>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && data && (
        <>
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Solde projeté</h2>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.periodes}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="periode" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip formatter={(v: any) => `${Number(v).toFixed(3)} TND`} />
                <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="soldeProjete" stroke="#1FA94B" strokeWidth={2} name="Solde projeté" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="card overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-4 py-2">Mois</th>
                  <th className="px-4 py-2 text-right">Encaissements</th>
                  <th className="px-4 py-2 text-right">Décaissements</th>
                  <th className="px-4 py-2 text-right">Net</th>
                  <th className="px-4 py-2 text-right">Solde projeté</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.periodes.map((p) => (
                  <tr key={p.periode}>
                    <td className="px-4 py-2 font-medium">{p.periode}</td>
                    <td className="px-4 py-2 text-right text-green-700"><MoneyDisplay amount={p.encaissements} size="sm" /></td>
                    <td className="px-4 py-2 text-right text-red-700"><MoneyDisplay amount={p.decaissements} size="sm" /></td>
                    <td className="px-4 py-2 text-right"><MoneyDisplay amount={p.net} size="sm" /></td>
                    <td className={`px-4 py-2 text-right font-medium ${p.soldeProjete < 0 ? 'text-red-600' : ''}`}>
                      <MoneyDisplay amount={p.soldeProjete} size="sm" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default Tresorerie;
